import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';

import TextInput from './TextInput.jsx';
import DateTimePicker from './DateTimePicker';
import Button from './Button';

// P3 (PM analitik tamamlama, 2026-10-08) — kilometre taşı hedefinin adım düzenleyicisi
// (TaskTemplateItemsEditor deseni: kontrollü bileşen, değer = AYRIŞTIRILMIŞ dizi).
//   value: [{ key, id?, title, weightPct, dueDate? }]
// Kural (analiz §22 + backend GoalTypeRules.ValidateMilestoneSteps): en az 1 adım, her adımın
// başlığı dolu, 0 < ağırlık ≤ 100, ağırlıkların TOPLAMI TAM 100. Toplam satırı anlık gösterilir;
// kayıt kapısı backend'de (toplam tutmazsa kayıt reddedilir). `showDueDate` false iken (şablon)
// tarih alanı çizilmez. `minDate`/`maxDate` dönem aralığıdır (adım son tarihi dönem içinde olmalı).
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StepCard = styled.div`
  display: grid;
  grid-template-columns: ${({ $withDate }) => ($withDate ? 'minmax(0, 2fr) 110px minmax(0, 1fr) auto' : 'minmax(0, 2fr) 110px auto')};
  gap: 10px;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: ${({ theme }) => theme.metrics.radius.large};
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.bgColor};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Total = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme, $valid }) => ($valid ? theme.colors.secondary.dark : theme.colors.auxiliar.danger)};
`;

const ErrorText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.auxiliar.danger};
`;

const round2 = (v) => Math.round(Number(v) * 100) / 100;

/** Adım ağırlıklarının toplamı (2 ondalık). Boş/geçersiz ağırlık 0 sayılır. */
export const milestoneStepsTotal = (items) => round2((items || [])
  .reduce((sum, it) => sum + (Number.isFinite(Number(it.weightPct)) ? Number(it.weightPct) : 0), 0));

/** Backend kapısının istemci ikizi: ≥1 adım, başlık dolu, 0 < w ≤ 100, toplam TAM 100. */
export const isMilestoneStepsValid = (items) => {
  const list = items || [];
  if (list.length === 0) return false;
  const eachValid = list.every((it) => (it.title || '').trim() !== ''
    && Number(it.weightPct) > 0 && Number(it.weightPct) <= 100);
  return eachValid && milestoneStepsTotal(list) === 100;
};

let keySeed = 0;
const nextKey = () => {
  keySeed += 1;
  return `step-${Date.now().toString(36)}-${keySeed}`;
};

/** API adımlarını (MilestoneStepResponse / şablon adımı) düzenleyici değerine çevirir. */
export const fromMilestoneSteps = (steps) => (steps || []).map((s) => ({
  key: s.id || nextKey(),
  id: s.id,
  title: s.title || '',
  weightPct: s.weightPct != null ? String(s.weightPct) : '',
  dueDate: s.dueDate ? String(s.dueDate).slice(0, 10) : null,
}));

/** Düzenleyici değerini istek gövdesine çevirir (MilestoneStepInput). */
export const toMilestoneStepsPayload = (items, { withIds = true, withDueDate = true } = {}) => (items || [])
  .map((it) => ({
    ...(withIds && it.id ? { id: it.id } : {}),
    title: (it.title || '').trim(),
    weightPct: Number(it.weightPct),
    ...(withDueDate ? { dueDate: it.dueDate || undefined } : {}),
  }));

/** Yeni boş adım. */
export const emptyMilestoneStep = () => ({ key: nextKey(), title: '', weightPct: '', dueDate: null });

export default function MilestoneStepsEditor({
  label, value, onChange, error, showDueDate = true, minDate, maxDate,
}) {
  const { t } = useTranslation();
  const items = Array.isArray(value) ? value : [];
  const total = milestoneStepsTotal(items);
  const totalValid = items.length > 0 && total === 100;

  const update = (index, patch) => onChange(items.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  const remove = (index) => onChange(items.filter((_, i) => i !== index));
  const add = () => onChange([...items, emptyMilestoneStep()]);

  return (
    <Wrapper data-role="milestone-steps-editor">
      {label && <Label>{label}</Label>}
      {items.map((item, index) => (
        <StepCard key={item.key || item.id || index} $withDate={showDueDate} data-role={`milestone-step-${index}`}>
          <TextInput
            label={t('performance.goal.milestone.stepTitle', { index: index + 1 })}
            value={item.title || ''}
            onChange={(e) => update(index, { title: e.target.value })}
            required
          />
          <TextInput
            label={t('performance.goal.milestone.stepWeight')}
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={item.weightPct ?? ''}
            onChange={(e) => update(index, { weightPct: e.target.value })}
            data-role={`milestone-step-weight-${index}`}
          />
          {showDueDate && (
            <DateTimePicker
              label={t('performance.goal.milestone.stepDueDate')}
              mode="date"
              value={item.dueDate || null}
              onChange={(v) => update(index, { dueDate: v || null })}
              minDate={minDate}
              maxDate={maxDate}
            />
          )}
          <Button
            type="button"
            variant="ghost"
            onClick={() => remove(index)}
            aria-label={t('common.delete')}
            data-action={`remove-step-${index}`}
          >
            <Trash2 size={16} />
          </Button>
        </StepCard>
      ))}

      <Total $valid={totalValid} data-role="milestone-steps-total">
        {t('performance.goal.milestone.weightTotal', { total })}
        {!totalValid && items.length > 0 ? ` · ${t('performance.goal.milestone.weightTotalHint')}` : ''}
      </Total>
      {error && <ErrorText>{error}</ErrorText>}

      <div>
        <Button type="button" variant="outline" onClick={add} data-action="add-step">
          + {t('performance.goal.milestone.addStep')}
        </Button>
      </div>
    </Wrapper>
  );
}
